import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  PointerEvent as ReactPointerEvent
} from 'react';

import type { ErrorSchema } from '@rjsf/utils';
import type { GridChildComponentProps } from 'react-window';

import { Checkbox } from '@/shadcn/checkbox';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from '@/shadcn/hover-card';
import { cn } from '@/shadcn/lib/utils';

import type { TableErrors, TableRow } from '@/components/ui/table/table.type';
import { TableCellError } from '@/components/ui/table/tableCell/error.tableCell';
import { RowErrorIndicator } from '@/components/ui/table/tableCell/rowErrorIndicator';
import { InlineStyle } from '@/components/ui/utils/inlineStyle/inlineStyle';
import { Widget } from '@/components/ui/widget';
import type { WidgetUpdate } from '@/components/ui/widget/widget.type';
import { DATA_EDITOR_UPDATE } from '@/constant/dataEditor';
import { deepGet } from '@/utils';
import { executeStringFn } from '@/utils/executeFn';
import { getByPath } from '@/utils/widgets';
import { useTableContext } from '../tableContext';
import { GroupButton } from './groupButton/groupButton.cell';

// // check whether TableCell props are equal and no re-rendering is required
// const shallowCompareProps = (obj1, obj2) =>
//   Object.keys(obj1).length === Object.keys(obj2).length &&
//   Object.keys(obj1).every(key => obj1[key] === obj2[key]);

// const areEqual = (prevProps, nextProps) => {
//   // check style and tableContext shallow contents
//   const {
//     style: prevStyle,
//     tableContext: prevTableContext,
//     ...prevRest
//   } = prevProps;
//   const {
//     style: nextStyle,
//     tableContext: nextTableContext,
//     ...nextRest
//   } = nextProps;
//   return (
//     shallowCompareProps(prevRest, nextRest) &&
//     shallowCompareProps(prevStyle, nextStyle) &&
//     shallowCompareProps(prevTableContext, nextTableContext)
//   );
// };

// Check if the current cell has an error in the errorSchema
const getCellErrorMessages = (
  errorSchema: TableErrors | undefined,
  rowSelector: string,
  columnKey: string
) => {
  if (!errorSchema) return [];
  const currentLine = errorSchema[rowSelector];
  if (!currentLine) return [];
  return currentLine[columnKey]?.__errors ?? [];
};

const getRowHasError = (
  errorSchema: Maybe<TableErrors>,
  rowSelector: string
) => {
  if (!errorSchema) return false;
  const currentLine = errorSchema[rowSelector];
  if (!currentLine || typeof currentLine !== 'object') return false;
  return (
    (currentLine?.__errors && currentLine?.__errors?.length > 0) ||
    Object.values(currentLine).some(line => {
      if (Array.isArray(line)) return line.length > 0;
      if (line?.__errors && Array.isArray(line.__errors))
        return line.__errors.length > 0;
      return false;
    })
  );
};

const getRowErrors = (
  errorSchema: Maybe<TableErrors>,
  rowSelector: string
): ErrorSchema<TableRow> => {
  if (!errorSchema) return {};
  const currentRowErrors = errorSchema[rowSelector];
  if (!currentRowErrors || typeof currentRowErrors !== 'object') return {};
  return currentRowErrors;
};

type SelectionModifiers = {
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
};

const createDefaultSelectionModifiers = (): SelectionModifiers => ({
  ctrlKey: false,
  metaKey: false,
  shiftKey: false
});

export const TableCell = memo(
  ({ rowIndex, columnIndex, style }: GridChildComponentProps) => {
    const {
      loading,
      isFixedGrid,
      data,
      columns,
      allColumns,
      isEditing,
      selectedData,
      allowRowsSelection,
      selectionReadOnly,
      dataSelectorPath,
      toggleRowSelected,
      pagination,
      displayDiffs,
      group,
      edit: { modifiedData, onDataModified } = {},
      rowInlineStyle,
      errorSchema
    } = useTableContext();
    const selectionModifiersRef = useRef<SelectionModifiers>(
      createDefaultSelectionModifiers()
    );
    const updateSelectionModifiers = useCallback(
      (event: ReactPointerEvent<Element> | ReactKeyboardEvent<Element>) => {
        selectionModifiersRef.current = {
          ctrlKey: event.ctrlKey,
          metaKey: event.metaKey,
          shiftKey: event.shiftKey
        };
      },
      []
    );

    if (pagination) {
      const { buffer, hasNextPage, onLoadMore } = pagination;

      if (!loading && hasNextPage && rowIndex >= data.length - buffer) {
        onLoadMore();
      }
    }
    let rowData: Nullish<TableRow> = null;
    let groupData: Nullable<TableRow> = null;
    let isSelectable = false;
    let groupButton: ReactNode = null;
    let isGroupHeaderRow = false;

    const isGroupUncollapsed =
      group && group.display && group.display.selected !== undefined;
    // We use lodash.get instead of getByPath for group indexes because getByPath yields `undefined`
    // on null values, and we need to distinguish the group.display.selected === null from the
    // group.display.selected === undefined case
    const selectedRowIndex = isGroupUncollapsed
      ? data.findIndex(
          d => deepGet(d, group.indexPath ?? '') === group.display?.selected
        )
      : null;
    if (
      isGroupUncollapsed &&
      selectedRowIndex !== null &&
      selectedRowIndex >= 0
    ) {
      /**
       * If a group has been uncollapsed, its underlying rows have to be integrated in the data row count
       * e.g. with group.display.selected = 'group2' and groups = [
       *   { index: "group1", rows: ["row1", "row2"] },
       *   { index: "group2", rows: ["row3", "row4"] },
       *   { index: "group3", rows: ["row5"] }
       * ]:
       * - selectedRowIndex = 1, corresponding to the 2nd item with index 'group2'
       * - with rowIndex = 0, the row should be "group1" (which is collapsed).
       *   rowIndex - selectedRowIndex = -1, and groupRows[-1] = undefined,
       *   0 < selectedRowIndex and data[0] = "group1".
       * - with rowIndex = 1, the row should be "row3" from "group2", which is uncollapsed.
       *   Since it's the first row of the group, it should have a collapse button as well.
       *   rowIndex - selectedRowIndex = 0, and groupRows[0] = "row3"
       * - with rowIndex = 2, the row should be "row4" from "group2"
       *   rowIndex - selectedRowIndex = 1, and groupRows[1] = "row4"
       * - with rowIndex = 3, the row should be "group3" (which is collapsed)
       *   rowIndex - selectedRowIndex = 2, and groupRows[2] = undefined,
       *   3 > selectedRowIndex, rowIndex - groupRows.length + 1 = 2, and data[2] = "group3"
       */
      const selectedGroupRows = getByPath(
        data[selectedRowIndex],
        group.rowsPath
      );
      const subRowIndex = rowIndex - selectedRowIndex;
      // If the rowIndex is inside the uncollapsed group, get the underlying row
      if (selectedGroupRows[subRowIndex]) {
        isSelectable = true;
        rowData = selectedGroupRows[subRowIndex];
        if (subRowIndex === 0) {
          groupButton = (
            <GroupButton
              onToggle={() => group.toggle(group.display?.selected)}
            />
          );
        }
      } else {
        let groupIndex = rowIndex;
        if (rowIndex > selectedRowIndex) {
          groupIndex += 1 - selectedGroupRows.length;
        }
        groupData = data[groupIndex];
        // Get the first row of the group for row display
        [rowData] = getByPath(groupData, group.rowsPath) || [];
        const groupRowCount = getByPath(groupData, group.rowCountPath);
        groupButton = (
          <GroupButton
            collapsed
            onToggle={() =>
              group.toggle(deepGet(groupData, group.indexPath ?? ''))
            }
            rowCount={groupRowCount}
          />
        );
      }
    } else if (group) {
      // If collapsed group
      groupData = data[rowIndex];
      isGroupHeaderRow = true;
      // Get the first row of the group for row display
      [rowData] = getByPath(groupData, group.rowsPath) || [];
      const groupRowCount = getByPath(groupData, group.rowCountPath);
      groupButton = (
        <GroupButton
          collapsed
          onToggle={() =>
            group.toggle(deepGet(groupData, group.indexPath ?? ''))
          }
          rowCount={groupRowCount}
        />
      );
    } else {
      // If regular row
      isSelectable = true;
      rowData = data[rowIndex];
    }

    // get the value of the row selector
    const rowId = rowData && getByPath(rowData, dataSelectorPath);

    // check if the row is selected
    const isSelected = allowRowsSelection && selectedData.includes(rowId);

    // provide sub-components like views and wrappers
    // with a way to let this cell know that a patch is being applied
    const [isPatched, setIsPatched] = useState(false);

    // check if selector cell
    let cellContent: ReactNode = null;
    const patch = modifiedData && rowId ? modifiedData[rowId] : null;

    const rowHasError = useMemo(
      () => getRowHasError(errorSchema, rowId),
      [errorSchema, rowId]
    );
    // Initialize hasError variable
    let cellErrorMessages: string[] = [];
    /**
     * @type {{key: string, def: any} | undefined}
     */
    const column = columns[columnIndex - (isFixedGrid ? 1 : 0)];

    const renderIndicator = ({
      isPatch = false,
      isError = false,
      isInitialItem = false
    }) => {
      if (isError) {
        const rowErrorMessages = getRowErrors(errorSchema, rowId);

        return (
          <RowErrorIndicator
            columns={allColumns}
            rowErrorSchema={rowErrorMessages}
          />
        );
      }
      if (isPatch) {
        return (
          <div
            className={cn(
              'absolute top-0 left-0 z-10 h-full w-[2px] bg-secondary',
              {
                'bg-accent': isInitialItem
              }
            )}
          />
        );
      }
      return null;
    };

    if (isFixedGrid && columnIndex === 0) {
      const toggleRowSelection = useCallback(() => {
        const { ctrlKey, metaKey, shiftKey } = selectionModifiersRef.current;
        toggleRowSelected(rowId, {
          rowIndex,
          ctrlKey,
          metaKey,
          shiftKey
        });
        selectionModifiersRef.current = createDefaultSelectionModifiers();
      }, [rowId, rowIndex, toggleRowSelected]);
      const checkContent = allowRowsSelection ? (
        <>
          {renderIndicator({ isPatch: !!patch, isError: rowHasError })}
          <Checkbox
            checked={isSelected}
            className="size-4"
            data-cy={`table-cell-select-row-${rowIndex}`}
            disabled={!rowId || selectionReadOnly}
            onCheckedChange={selectionReadOnly ? undefined : toggleRowSelection}
            onKeyDown={selectionReadOnly ? undefined : updateSelectionModifiers}
            onPointerDown={
              selectionReadOnly ? undefined : updateSelectionModifiers
            }
          />
        </>
      ) : (
        <div className="truncate">{rowIndex + 1}</div>
      );

      // Only show fillers in first columns if a group is uncollapsed
      cellContent = (
        <div className="flex size-full flex-row items-center justify-center overflow-hidden border-border border-r p-1">
          {groupButton || (isGroupUncollapsed && <div className="w-[29px]" />)}
          {isSelectable
            ? checkContent
            : isGroupUncollapsed && <div className="w-[18px]" />}
        </div>
      );
    } else {
      // real table cell

      const { key, def } = columns[columnIndex - (isFixedGrid ? 1 : 0)];

      let cell = def?.cell;
      const cellFn = def?.cellFn;

      cellErrorMessages = useMemo(
        () => getCellErrorMessages(errorSchema, rowId, key),
        [errorSchema, rowId, key]
      );

      // if grouping is enabled and the group is collapsed,
      // only display the data of the specified column and display "..." in the other columns
      if (groupData && group?.display?.column && group.display.column !== key) {
        cell = {
          view: {
            type: 'text',
            path: "'...'"
          }
        };
      }

      // bind the onChange function to the current row selector
      const onChange = useCallback(
        modifiedCellData => {
          onDataModified?.({
            type: DATA_EDITOR_UPDATE,
            rowSelector: rowId,
            dataToChange: modifiedCellData
          });
        },
        [rowId, onDataModified]
      );

      // if no patch provided set is patched to false
      useEffect(() => {
        if (!patch) setIsPatched(false);
      }, [patch]);

      // bind an update object with the patch and the updater
      const update: WidgetUpdate = useMemo(
        () => ({
          onChange: onDataModified ? onChange : null,
          patch,
          setIsPatched
        }),
        [onDataModified, onChange, patch]
      );

      if (rowData) {
        if (displayDiffs) {
          // display cell with diffs
          cellContent = (
            <div className="flex size-full flex-col flex-nowrap">
              <div className="relative basis-1/2 font-normal text-foreground">
                <Widget
                  // @ts-expect-error Widget is not typed, yet
                  className={cn(
                    'box-border flex size-full cursor-default flex-row flex-wrap items-center overflow-hidden border-border border-r px-[6px] text-[13px]',
                    'data-[is-hoverable=true]:cursor-pointer hover:data-[is-hoverable=true]:border-2 hover:data-[is-hoverable=true]:border-primary hover:data-[is-hoverable=true]:px-[4px]',
                    'data-[is-focus=true]:cursor-pointer hover:data-[is-focus=true]:border-2 hover:data-[is-focus=true]:border-primary hover:data-[is-focus=true]:px-[4px]'
                  )}
                  data={rowData}
                  disableWrappers={isEditing || isGroupHeaderRow}
                  renderContext="table-cell"
                  widget={cell}
                />
                {renderIndicator({
                  isPatch: isPatched,
                  isInitialItem: true
                })}
              </div>
              <div className="relative basis-1/2">
                <Widget
                  // @ts-expect-error Widget is not typed, yet
                  className={cn(
                    'box-border flex size-full cursor-default flex-row flex-wrap items-center overflow-hidden border-border border-r px-[6px] text-[13px]',
                    'data-[is-hoverable=true]:cursor-pointer hover:data-[is-hoverable=true]:border-2 hover:data-[is-hoverable=true]:border-primary hover:data-[is-hoverable=true]:px-[4px]',
                    'data-[is-focus=true]:cursor-pointer hover:data-[is-focus=true]:border-2 hover:data-[is-focus=true]:border-primary hover:data-[is-focus=true]:px-[4px]'
                  )}
                  data={rowData}
                  disableWrappers={isEditing || isGroupHeaderRow}
                  renderContext="table-cell"
                  update={update}
                  widget={cell}
                />
                {renderIndicator({
                  isPatch: isPatched
                })}
              </div>
            </div>
          );
        } else {
          // simple cell display
          if (cellFn) {
            cell = executeStringFn(rowData, cellFn);
          }

          cellContent = (
            <>
              {renderIndicator({
                // isError: cellErrorMessages.length > 0,
                isPatch: isPatched
              })}
              <Widget
                // @ts-expect-error Widget is not typed, yet
                className={cn(
                  'box-border flex size-full cursor-default flex-row flex-wrap items-center overflow-hidden border-border border-r px-[6px] text-[13px]',
                  'data-[no-padding=true]:p-0',
                  'data-[is-hoverable=true]:cursor-pointer hover:data-[is-hoverable=true]:border-2 hover:data-[is-hoverable=true]:border-primary hover:data-[is-hoverable=true]:px-[4px]',
                  'data-[is-focus=true]:cursor-pointer hover:data-[is-focus=true]:border-2 hover:data-[is-focus=true]:border-primary hover:data-[is-focus=true]:px-[4px]'
                )}
                data={rowData}
                disableWrappers={isEditing || isGroupHeaderRow}
                renderContext="table-cell"
                update={update}
                widget={cell}
              />
            </>
          );
        }
        // wrap with inline style
        if (rowInlineStyle) {
          cellContent = (
            <InlineStyle
              className="h-full"
              data={rowData}
              rules={rowInlineStyle}
            >
              {cellContent}
            </InlineStyle>
          );
        }
      } else {
        cellContent = (
          <div
            className={cn(
              'box-border flex size-full cursor-default flex-row flex-wrap items-center overflow-hidden border-border border-r px-[6px] text-[13px]',
              'data-[is-hoverable=true]:cursor-pointer hover:data-[is-hoverable=true]:border-2 hover:data-[is-hoverable=true]:border-primary hover:data-[is-hoverable=true]:px-[4px]',
              'data-[is-focus=true]:cursor-pointer hover:data-[is-focus=true]:border-2 hover:data-[is-focus=true]:border-primary hover:data-[is-focus=true]:px-[4px]'
            )}
          />
        );
      }
    }
    const isEvenRow = !(rowIndex % 2);
    const cellHasError = cellErrorMessages.length > 0;

    return (
      <div
        className={cn(
          'relative',
          isPatched && 'font-semibold',
          isEvenRow && 'bg-accent/50',
          isEvenRow &&
            cellHasError &&
            'bg-destructive/15 dark:bg-destructive/20',
          !isEvenRow &&
            cellHasError &&
            'bg-destructive/10 dark:bg-destructive/15',
          isSelected &&
            'bg-primary-deep/10 text-accent-foreground dark:bg-primary/15',
          isSelected && isEvenRow && 'bg-primary-deep/15 dark:bg-primary/25'
        )}
        data-cy={`table-cell-column_${column?.key ?? 'selectRow'}-row_${rowIndex}`}
        data-is-evenrow={isEvenRow}
        data-is-patched={isPatched}
        data-is-selected={isSelected}
        style={style}
      >
        {cellContent}
        {cellHasError && (
          <HoverCard openDelay={0}>
            <HoverCardTrigger asChild>
              <div className="clip- absolute top-0 left-0 z-10 size-2.5 cursor-pointer rounded-br-full bg-destructive/90 text-destructive-foreground text-xs leading-0 hover:bg-destructive" />
            </HoverCardTrigger>
            <HoverCardContent className="w-fit max-w-64 p-2">
              <TableCellError
                cellErrorMessages={cellErrorMessages}
                columnKey={column?.key}
              />
            </HoverCardContent>
          </HoverCard>
        )}
      </div>
    );
  }
);
